import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider, useLanguage, ISO_LANG } from "./context/LanguageContext";
import SEO from "./seo/seoTexts.json";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import { Toaster } from "sonner";

// Pages
import Home from "./pages/Home";
import Villa from "./pages/Villa";
import Bedrooms from "./pages/Bedrooms";
import Amenities from "./pages/Amenities";
import Gallery from "./pages/Gallery";
import Booking from "./pages/Booking";
import Reviews from "./pages/Reviews";
import Location from "./pages/Location";
import Contact from "./pages/Contact";
import Policies from "./pages/Policies";
import Host from "./pages/Host";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// <head> ανά σελίδα + γλώσσα, με τα εγκεκριμένα SEO κείμενα (src/seo/seoTexts.json).
// Η πρώτη φόρτωση έρχεται ήδη prerendered από το nginx (scripts/prerender.js)· εδώ κρατάμε
// το <head> σωστό όταν ο επισκέπτης αλλάζει σελίδα ή γλώσσα μέσα στην εφαρμογή.
// Τίτλος/περιγραφή: στη γλώσσα που βλέπει ο επισκέπτης.
// Canonical/hreflang: από το URL (?lang=), με τον ίδιο κανόνα με prerender και sitemap.
const SEO_PAGES = Object.fromEntries(SEO.pages.map((p) => [p.path, p]));

const setMeta = (attr, name, content) => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const addLink = (attrs) => {
  const el = document.createElement("link");
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
};

const SeoHead = () => {
  const { pathname, search } = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    const page = SEO_PAGES[pathname.replace(/\/+$/, "") || "/"];
    if (!page) return; // route εκτός λίστας: μένει το shell

    const text = page.texts[ISO_LANG[language]] || page.texts[SEO.defaultLang];
    document.title = text.title;
    setMeta("name", "description", text.description);
    setMeta("property", "og:title", text.title);
    setMeta("property", "og:description", text.description);

    let urlLang = (new URLSearchParams(search).get("lang") || "").toLowerCase();
    if (urlLang === "gr") urlLang = "el";
    const base = SEO.origin + page.path;
    const canonical = SEO.langs.includes(urlLang) ? `${base}?lang=${urlLang}` : base;
    document.head
      .querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());
    addLink({ rel: "canonical", href: canonical });
    SEO.langs.forEach((hl) => addLink({ rel: "alternate", hreflang: hl, href: `${base}?lang=${hl}` }));
    addLink({ rel: "alternate", hreflang: "x-default", href: base });
    setMeta("property", "og:url", canonical);
  }, [pathname, search, language]);

  return null;
};

// Facebook Pixel Placeholder
const FacebookPixel = () => {
  useEffect(() => {
    // Facebook Pixel Code Placeholder
    // Replace 'YOUR_PIXEL_ID' with your actual Facebook Pixel ID when ready
    console.log('Facebook Pixel placeholder initialized');
  }, []);

  return null;
};

function App() {
  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <ScrollToTop />
          <SeoHead />
          <FacebookPixel />
          <Navigation />
          <Toaster position="top-center" richColors />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/villa" element={<Villa />} />
              <Route path="/bedrooms" element={<Bedrooms />} />
              <Route path="/amenities" element={<Amenities />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/location" element={<Location />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/policies" element={<Policies />} />
              <Route path="/host" element={<Host />} />
            </Routes>
          </main>
          <Footer />
          <WhatsAppButton />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
